import React, { useEffect, useState, useCallback } from "react";
import { api, apiErr } from "../../lib/api";
import { toast } from "sonner";
import { Download, LogIn, LogOut, Landmark } from "lucide-react";

const eur = (n) => (n === null || n === undefined) ? "—" : new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n) + " €";
const today = () => { const d = new Date(); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 10); };
const frDate = (s) => { try { return new Date(s + "T12:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" }); } catch { return s; } };

const SRC = {
  Airbnb: "bg-rose-50 text-rose-600 ring-rose-200",
  Booking: "bg-blue-50 text-blue-700 ring-blue-200",
  Site: "bg-brand-50 text-brand ring-brand/20",
  "Téléphone": "bg-amber-50 text-amber-700 ring-amber-200",
  Manuel: "bg-slate-100 text-slate-600 ring-slate-200",
};

const SourceBadge = ({ s }) => (
  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ring-1 ring-inset ${SRC[s] || "bg-slate-100 text-slate-600 ring-slate-200"}`}>{s}</span>
);

export default function ArrivalsTab() {
  const [date, setDate] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback((d) => {
    setLoading(true);
    api.get(`/admin/arrivals?date=${d}`).then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(date); }, [date, load]);

  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/admin/arrivals.pdf?date=${date}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `arrivees-departs-${date}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const arrivals = data?.arrivals || [];
  const departures = data?.departures || [];

  return (
    <div className="space-y-5" data-testid="arrivals-tab">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <label className="text-xs font-semibold text-slate-500 block mb-1">Date du rapport</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="arrivals-date"
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40" />
          <p className="text-xs text-slate-400 mt-1 capitalize">{frDate(date)}</p>
        </div>
        <button onClick={download} disabled={busy} data-testid="arrivals-download-pdf"
          className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white px-4 py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50">
          <Download size={15} /> {busy ? "Génération…" : "Télécharger le PDF (paysage)"}
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-2xl border border-black/5 p-4" data-testid="arrivals-stat-count">
          <LogIn size={18} className="text-emerald-600 mb-2" strokeWidth={1.5} />
          <div className="font-heading text-2xl text-ink-900 tabular-nums">{data?.arrivals_count ?? 0}</div>
          <div className="text-xs text-ink-400 mt-0.5">Arrivées ce jour</div>
        </div>
        <div className="bg-white rounded-2xl border border-black/5 p-4" data-testid="arrivals-stat-departures">
          <LogOut size={18} className="text-rose-500 mb-2" strokeWidth={1.5} />
          <div className="font-heading text-2xl text-ink-900 tabular-nums">{data?.departures_count ?? 0}</div>
          <div className="text-xs text-ink-400 mt-0.5">Départs ce jour</div>
        </div>
        <div className="bg-white rounded-2xl border border-black/5 p-4" data-testid="arrivals-stat-tax">
          <Landmark size={18} className="text-brand mb-2" strokeWidth={1.5} />
          <div className="font-heading text-2xl text-ink-900 tabular-nums">{eur(data?.tax_total)}</div>
          <div className="text-xs text-ink-400 mt-0.5">Taxe de séjour à percevoir</div>
        </div>
      </div>

      {loading ? <p className="p-8 text-center text-slate-400">Chargement…</p> : (
        <>
          {/* ARRIVÉES */}
          <div>
            <h3 className="flex items-center gap-2 font-heading text-lg text-emerald-700 mb-2"><LogIn size={18} /> Arrivées ({arrivals.length})</h3>
            <div className="bg-white rounded-xl border border-emerald-100 shadow-sm overflow-hidden">
              {arrivals.length === 0 ? (
                <p className="p-6 text-center text-slate-400" data-testid="arrivals-empty">Aucune arrivée prévue ce jour.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-emerald-50/70 text-slate-500 text-xs">
                      <tr>
                        {["Voyageur", "Logement", "Nuitées", "Source", "Reste à payer sur place", "Taxe séjour", "Téléphone"].map((h, i) => (
                          <th key={h} className={`font-semibold px-4 py-3 whitespace-nowrap ${(i === 2 || i === 4 || i === 5) ? "text-right" : "text-left"}`}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {arrivals.map((r, i) => (
                        <tr key={i} className="border-t border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`arrival-row-${i}`}>
                          <td className="px-4 py-3 font-medium text-slate-900">{r.guest}</td>
                          <td className="px-4 py-3 text-slate-600">{r.logement}</td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-600">{r.nights}</td>
                          <td className="px-4 py-3"><SourceBadge s={r.source} /></td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-600">{r.balance_due === null ? <span className="text-slate-300">payé</span> : eur(r.balance_due)}</td>
                          <td className="px-4 py-3 text-right tabular-nums font-medium text-slate-900">{eur(r.tourist_tax)}</td>
                          <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{r.phone || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* DÉPARTS */}
          <div>
            <h3 className="flex items-center gap-2 font-heading text-lg text-rose-600 mb-2"><LogOut size={18} /> Départs ({departures.length})</h3>
            <div className="bg-white rounded-xl border border-rose-100 shadow-sm overflow-hidden">
              {departures.length === 0 ? (
                <p className="p-6 text-center text-slate-400" data-testid="departures-empty">Aucun départ prévu ce jour.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-rose-50/70 text-slate-500 text-xs">
                      <tr>
                        {["Voyageur", "Logement", "Nuitées", "Source", "Téléphone"].map((h, i) => (
                          <th key={h} className={`font-semibold px-4 py-3 whitespace-nowrap ${i === 2 ? "text-right" : "text-left"}`}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {departures.map((r, i) => (
                        <tr key={i} className="border-t border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`departure-row-${i}`}>
                          <td className="px-4 py-3 font-medium text-slate-900">{r.guest}</td>
                          <td className="px-4 py-3 text-slate-600">{r.logement}</td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-600">{r.nights}</td>
                          <td className="px-4 py-3"><SourceBadge s={r.source} /></td>
                          <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{r.phone || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      <p className="text-xs text-slate-400 leading-relaxed">
        Taxe de séjour calculée à <b>1,25 € par adulte et par nuit</b> (enfants exonérés). Le « reste à payer sur place » = total du séjour moins l'acompte déjà réglé en ligne ; il ne concerne que les réservations directes (site / téléphone) ; Airbnb et Booking sont déjà réglés via la plateforme. Les séjours déjà en cours (ni arrivée ni départ ce jour) ne sont pas listés. La colonne <b>Heure d'arrivée / de départ</b> figure sur le PDF pour être remplie à la main.
      </p>
    </div>
  );
}
